import React, { useState, useEffect } from 'react';
import { X, Edit2, MapPin, Mail, User, FileText, Pencil, BadgeCheck } from 'lucide-react';
import EditProfileModal from './EditProfileModal';
import RestaurantCard from './RestaurantCard';
import { useAuth } from '../../Context/AuthContext';
import { getProfile } from '../../lib/api/user.apis';
import { useNavigate } from 'react-router-dom';
import { GiProgression } from 'react-icons/gi';
import { MdOutlineChangeCircle } from 'react-icons/md';

interface User {
  uid: string;
  name: string;
  email: string;
  currentCity?: string;
  about?: string;
  hasClaimedRestaurants?: boolean;
  hasActiveClaimOrTransfer?: boolean;
  hasInvite: boolean;
  inviteToken?: string;
  inviteExpiresAt?: string;
  inviteRestaurantId?: string;
  inviteRestaurantName?: string;
  activeProcess?: {
    requestId: string;
    restaurantId: string;
    restaurantName: string;
    status: string;
    type: 'claim' | 'transfer';
  }
}

interface Photo {
  url: string;
  caption?: string;
  category?: string;
  uploadedAt: string;
}

interface Upload {
  restaurantId: string;
  restaurantName: string;
  restaurantCover?: string;
  photos: Photo[];
}

interface Pagination {
  page: number;
  limit: number;
  totalRestaurants: number;
  totalPages: number;
}

interface ProfileData {
  success: boolean;
  user: User;
  uploads: Upload[];
  pagination: Pagination;
  message?: string;
}

const UserProfile = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const { user, accessToken } = useAuth();

  // Fetch user profile on mount
  useEffect(() => {
    if (user?.id) {
      fetchProfile();
    }
  }, [user?.id, page]);

  const fetchProfile = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const data = await getProfile( page, 10, 12, accessToken || '');

      setProfile(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = (updatedUser: User) => {
    setProfile(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        user: updatedUser
      };
    });
  };

  if (loading && !profile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFD166] mx-auto"></div>
          <p className="mt-4 text-[#5E503F]">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Profile</h2>
          <p className="text-gray-600 mb-4">{error}: Check Connection and retry.</p>
          <button
            onClick={fetchProfile}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const { user: userData, uploads, pagination } = profile;
  const hasUploads = uploads && uploads.length > 0;

  return (
    <div className="min-h-auto bg-gray-50">
      {/* Header */}
      <div className="fixed top-17 right-0 left-0 bg-white border-b border-gray-200 z-17">
        <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-6 py-2">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl sm:text-2xl font-bold text-gray-900 mi-sans">Your Profile</h1>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-bold text-gray-700 border border-gray-300"
            >
              <Pencil className="w-4 h-4" />
              <span className="hidden sm:inline">Edit Profile</span>
              <span className="sm:hidden">Edit</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl pt-17 mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Info Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-8">
              <div className="space-y-5">
                {/* Username */}
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <User className="w-4 h-4" />
                    <span>Username</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-900">
                    {userData.name || 'Not set'}
                  </p>
                </div>

                {/* Current City */}
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <MapPin className="w-4 h-4" />
                    <span>Current City</span>
                  </div>
                  <p className="text-gray-900">
                    {userData.currentCity || 'Not set'}
                  </p>
                </div>

                {/* About */}
                <div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <FileText className="w-4 h-4" />
                    <span>About You</span>
                  </div>
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {userData.about || 'No bio yet'}
                  </p>
                </div>

                {/* Email (Read-only) */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <Mail className="w-4 h-4" />
                    <span>Email</span>
                  </div>
                  <p className="text-gray-900 break-all">
                    {userData.email}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Email cannot be changed
                  </p>
                </div>

                {userData.hasClaimedRestaurants && 
                <div className="pt-4 border-t border-gray-200 hover:bg-[var(--hover-color)] cursor-pointer" onClick={() => navigate('/dashboard')}>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <BadgeCheck className="w-4 h-4" />
                    <span>Claimed Restaurants</span>
                  </div>
                  <p className="text-gray-900 break-all">
                    Business dashboard
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    View and manage the restaurants you’ve claimed.
                  </p>
                </div>}

                {userData.hasActiveClaimOrTransfer && userData.activeProcess.type === "transfer" && 
                <div className="p-2 rounded pt-4 bg-green-100 border-t border-gray-200 hover:opacity-60 cursor-pointer" onClick={() => navigate(`/transfer/${userData.activeProcess.requestId}`)}>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                    <MdOutlineChangeCircle className="w-4 h-4 text-green-300" />
                    <span className='text-green-600 hover:underline'>Active Transfer</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    View status of your active transfer request for {userData.activeProcess.restaurantName}.
                  </p>
                </div>}
                
                {userData.hasInvite && 
                <div className="p-2 rounded pt-4 bg-blue-100 border-t border-gray-200 hover:opacity-60 cursor-pointer" onClick={() => navigate(`/restaurant-invite?token=${userData.inviteToken}`)}>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">    
                    <GiProgression className="w-4 h-4 text-blue-300" />
                    <span className='text-blue-600 hover:underline'>Pending Invite</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    You have a pending invite to claim <strong>{userData.inviteRestaurantName}</strong>. Click to view details.
                  </p>
                </div>
                }
              </div>
            </div>
          </div>

          {/* Restaurants Section */}
          <div className="lg:col-span-2">
            <div className="mb-2">
              <h2 className="text-xl font-bold text-gray-900">Your Uploaded Photos</h2>
              <p className="text-gray-600">
                Help other travelers see what you’ve seen, so they know what to expect!
              </p>
            </div>

            {!hasUploads ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <div className="max-w-sm mx-auto">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Post Your First Photo
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Share your dining experiences by uploading photos of your favorite restaurants.
                  </p>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="px-6 py-2 bg-[#ee5219] text-white rounded-lg hover:bg-[#d44a15] transition-colors"
                  >
                    Explore Restaurants
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {uploads.map((upload) => (
                  <RestaurantCard
                    key={upload.restaurantId}
                    restaurant={upload}
                  />
                ))}

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <span className="text-gray-600">
                      Page {page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                      disabled={page === pagination.totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {isEditModalOpen && user && (
        <EditProfileModal
          user={userData}
          userId={user.id}
          onClose={() => setIsEditModalOpen(false)}
          onUpdate={handleProfileUpdate}
        />
      )}
    </div>
  );
};

export default UserProfile;